import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

type SidebarItem =
  | { kind: 'link'; to: string; label: string; end?: boolean }
  | { kind: 'disabled'; label: string }
  | { kind: 'action'; label: string; action: 'signout'; danger?: boolean };

export function AdminLayout() {
  const { user, logout, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const manageNav = useMemo<SidebarItem[]>(() => {
    const items: SidebarItem[] = [
      { kind: 'link', to: '/', label: 'Console', end: true },
      { kind: 'link', to: '/users', label: 'Users' },
      { kind: 'link', to: '/role-change-requests', label: 'Role changes' }
    ];

    if (isSuperAdmin) {
      items.push({ kind: 'link', to: '/activity-logs', label: 'Activity Logs' });
      items.push({ kind: 'link', to: '/schools', label: 'Schools' });
      items.push({ kind: 'link', to: '/character-pillars', label: 'Character Pillars' });
      items.push({ kind: 'link', to: '/badges', label: 'Badges' });
    }

    return items;
  }, [isSuperAdmin]);

  const schoolNav = useMemo<SidebarItem[]>(() => {
    if (isSuperAdmin) return [];
    return [
      { kind: 'link', to: '/schools', label: 'My school' },
      { kind: 'link', to: '/classes', label: 'Classes' },
      { kind: 'link', to: '/content', label: 'Content' },
      { kind: 'link', to: '/registration', label: 'Registration' }
    ];
  }, [isSuperAdmin]);

  const accountNav: SidebarItem[] = [
    { kind: 'link', to: '/settings', label: 'Settings' },
    { kind: 'link', to: '/profile', label: 'Profile' },
    { kind: 'action', label: 'Sign out', action: 'signout', danger: true }
  ];

  useEffect(() => {
    if (!menuOpen) return;

    function handlePointerDown(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  function goTo(path: string) {
    setMenuOpen(false);
    setMobileNavOpen(false);
    navigate(path);
  }

  function handleSignOut() {
    setMenuOpen(false);
    setMobileNavOpen(false);
    logout();
  }

  function renderSidebarItem(item: SidebarItem) {
    if (item.kind === 'link') {
      return (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) => (isActive ? 'sidebar-link active' : 'sidebar-link')}
          onClick={() => setMobileNavOpen(false)}
        >
          {item.label}
        </NavLink>
      );
    }

    if (item.kind === 'disabled') {
      return (
        <span key={item.label} className="sidebar-link is-disabled" title="Coming soon">
          {item.label}
          <em>Soon</em>
        </span>
      );
    }

    return (
      <button
        key={item.label}
        type="button"
        className={
          item.danger ? 'sidebar-link sidebar-action danger' : 'sidebar-link sidebar-action'
        }
        onClick={handleSignOut}
      >
        {item.label}
      </button>
    );
  }

  return (
    <div className={`admin-app${mobileNavOpen ? ' nav-open' : ''}`}>
      {mobileNavOpen ? (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="Close navigation"
          onClick={() => setMobileNavOpen(false)}
        />
      ) : null}

      <aside className="admin-sidebar" aria-label="Admin navigation">
        <div className="sidebar-brand">
          <p className="brand-mark">The Foundation Movement</p>
          <strong>TFM Admin</strong>
        </div>

        <nav className="sidebar-nav">
          <p className="sidebar-section-label">Manage</p>
          {manageNav.map(renderSidebarItem)}

          {schoolNav.length > 0 ? (
            <>
              <p className="sidebar-section-label">School</p>
              {schoolNav.map(renderSidebarItem)}
            </>
          ) : null}

          <p className="sidebar-section-label">Account</p>
          {accountNav.map(renderSidebarItem)}
        </nav>
      </aside>

      <div className="admin-workspace">
        <header className="admin-topbar">
          <div className="topbar-leading">
            <button
              type="button"
              className="sidebar-toggle"
              aria-label={mobileNavOpen ? 'Close sidebar' : 'Open sidebar'}
              onClick={() => setMobileNavOpen((open) => !open)}
            >
              <span className="burger-line" />
              <span className="burger-line" />
              <span className="burger-line" />
            </button>
            <div>
              <p className="brand-mark topbar-mark">Admin portal</p>
              <h1>TFM Admin Portal</h1>
            </div>
          </div>

          <div className="admin-header-actions">
            <div className="admin-user">
              <strong>{user?.fullName}</strong>
              <span>
                {user?.email}
                {user?.role ? ` · ${user.role}` : ''}
              </span>
            </div>

            <div className="account-menu" ref={menuRef}>
              <button
                type="button"
                className="burger-btn"
                aria-label={menuOpen ? 'Close account menu' : 'Open account menu'}
                aria-expanded={menuOpen}
                aria-controls={menuId}
                onClick={() => setMenuOpen((open) => !open)}
              >
                <span className="burger-line" />
                <span className="burger-line" />
                <span className="burger-line" />
              </button>

              {menuOpen ? (
                <div id={menuId} className="account-menu-panel" role="menu">
                  <div className="account-menu-identity">
                    <strong>{user?.fullName}</strong>
                    <span>{user?.email}</span>
                    {user?.role ? <span>{user.role}</span> : null}
                  </div>
                  <button type="button" role="menuitem" onClick={() => goTo('/profile')}>
                    Profile
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => goTo('/settings#change-password')}
                  >
                    Change password
                  </button>
                  <button type="button" role="menuitem" onClick={() => goTo('/settings')}>
                    Settings
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="danger-item"
                    onClick={handleSignOut}
                  >
                    Sign out
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>

        <main className="admin-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
